import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  FormInput,
  FormPhoneInput,
  FormSelect,
  FormToggle,
  RemoveButton,
  SectionCard,
} from "components/BaseForm";
import { PHONE_TYPE_OPTIONS } from "routes/contact/form/constants";
import { BLANK_PHONE } from "routes/contact/form/defaults";
import {
  PrimaryToggle,
  useRemoveKeepingPrimary,
} from "routes/contact/form/sections/primaryRow";
import ReachabilityBadge from "routes/contact/form/sections/ReachabilityBadge";

const PhoneNumbersSection = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "phone_numbers",
  });
  const removeRow = useRemoveKeepingPrimary("phone_numbers", remove);
  const rows = useWatch({ control, name: "phone_numbers" });
  const isCallable = rows.some(row => row.enabled && row.is_callable);

  return (
    <SectionCard
      title={
        <span className="tw:flex tw:items-center tw:gap-2">
          Phone Numbers
          <ReachabilityBadge label="Callable" isReachable={isCallable} />
        </span>
      }
    >
      {fields.length > 0 && (
        <Table className="tw:w-auto">
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-72 tw:min-w-72">Number</TableHead>
              <TableHead className="tw:w-24 tw:min-w-24">Ext</TableHead>
              <TableHead className="tw:w-44">Type</TableHead>
              <TableHead className="tw:w-24">Primary</TableHead>
              <TableHead className="tw:w-24">Enabled</TableHead>
              <TableHead className="tw:w-24">Callable</TableHead>
              <TableHead className="tw:w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <FormPhoneInput
                    name={`phone_numbers.${index}.number`}
                    aria-label={`Phone ${index + 1}`}
                    placeholder="Enter phone number"
                  />
                </TableCell>
                <TableCell>
                  <FormInput
                    name={`phone_numbers.${index}.ext`}
                    aria-label={`Phone ${index + 1} extension`}
                    placeholder="Ext."
                  />
                </TableCell>
                <TableCell>
                  <FormSelect
                    name={`phone_numbers.${index}.type`}
                    options={PHONE_TYPE_OPTIONS}
                  />
                </TableCell>
                <TableCell>
                  <PrimaryToggle
                    arrayName="phone_numbers"
                    index={index}
                    label={`Phone ${index + 1} primary`}
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`phone_numbers.${index}.enabled`}
                    aria-label={`Phone ${index + 1} enabled`}
                  />
                </TableCell>
                <TableCell>
                  <FormToggle
                    name={`phone_numbers.${index}.is_callable`}
                    aria-label={`Phone ${index + 1} callable`}
                  />
                </TableCell>
                <TableCell>
                  <RemoveButton
                    label={`Remove phone ${index + 1}`}
                    onClick={() => removeRow(index)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tw:self-start"
        onClick={() => append({ ...BLANK_PHONE })}
      >
        <PlusIcon data-icon="inline-start" /> Add Phone
      </Button>
    </SectionCard>
  );
};

export default PhoneNumbersSection;
